import React, { useState } from 'react';
import { TMUA_PEARSON_TOKENS, getTmuaPearsonChrome } from '../constants/tmuaPearsonTokens';

function GearIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.03 7.03 0 0 0-1.63-.94l-.36-2.54A.5.5 0 0 0 13.9 2h-3.8a.5.5 0 0 0-.49.42l-.36 2.54c-.59.24-1.13.55-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.81 8.48a.5.5 0 0 0 .12.64L4.96 10.7c-.04.31-.06.63-.06.94s.02.63.06.94L2.93 14.16a.5.5 0 0 0-.12.64l1.92 3.32c.13.22.4.31.64.22l2.39-.96c.5.39 1.04.7 1.63.94l.36 2.54c.05.24.26.42.49.42h3.8c.24 0 .44-.18.49-.42l.36-2.54c.59-.24 1.13-.55 1.63-.94l2.39.96c.24.09.51 0 .64-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z" />
    </svg>
  );
}

export default function TmuaPearsonNavigatorModal({
  questions,
  currentQuestionIndex,
  visitedQuestions,
  flaggedQuestions,
  isQuestionAnswered,
  navigatorSortAsc,
  setNavigatorSortAsc,
  onJump,
  onClose,
  colorScheme,
  hideFlagged = false,
}) {
  const T = TMUA_PEARSON_TOKENS;
  const chrome = getTmuaPearsonChrome(colorScheme);
  const [size, setSize] = useState({ w: 640, h: 430 });

  const indexPairs = (questions || []).map((q, idx) => ({ q, idx }));
  if (!navigatorSortAsc) indexPairs.reverse();

  let unseenIncomplete = 0;
  (questions || []).forEach((q) => {
    if (!isQuestionAnswered(q)) unseenIncomplete += 1;
  });

  const th = {
    border: '1px solid #C5C5C5',
    padding: '5px 8px',
    background: T.toolbarBg,
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: 13,
    textAlign: 'left',
  };
  const tdBase = {
    border: '1px solid #C5C5C5',
    padding: '4px 8px',
    fontSize: 13,
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .tmua-pearson-navigator-modal {
            width: 100% !important;
            height: 100% !important;
            min-width: 0 !important;
            min-height: 0 !important;
          }
          .tmua-pearson-navigator-resize-handle {
            display: none !important;
          }
        }
      `}</style>
      <div
        className="tmua-pearson-navigator-modal"
        style={{
          width: size.w,
          height: size.h,
          minWidth: 480,
          minHeight: 280,
          background: chrome.titleBar,
          color: chrome.chromeFg,
          fontFamily: 'Tahoma, "Segoe UI", sans-serif',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
          pointerEvents: 'auto',
          position: 'relative',
        }}
      >
        <div
          style={{
            padding: '8px 10px 6px',
            fontSize: 13,
            fontWeight: 600,
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <GearIcon />
          <span>Navigator - select a question to go to it</span>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 8px 4px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#FFFFFF', color: '#000' }}>
            <thead>
              <tr>
                <th style={th}>
                  <button
                    type="button"
                    onClick={() => setNavigatorSortAsc((prev) => !prev)}
                    style={{
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'Tahoma, "Segoe UI", sans-serif',
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#FFFFFF',
                      padding: 0,
                    }}
                  >
                    Question # {navigatorSortAsc ? '▲' : '▼'}
                  </button>
                </th>
                <th style={th}>Status</th>
                {hideFlagged ? null : <th style={th}>Flagged for Review</th>}
              </tr>
            </thead>
            <tbody>
              {indexPairs.map(({ q, idx }, rowI) => {
                const answered = isQuestionAnswered(q);
                const status = answered ? 'Complete' : (visitedQuestions[q.id] ? 'Incomplete' : 'Unseen');
                const flagged = Boolean(flaggedQuestions[q.id]);
                const isCurrent = idx === currentQuestionIndex;
                const zebra = rowI % 2 === 1 ? T.navRowAlt : '#FFFFFF';
                const bg = isCurrent ? T.navRowCurrent : zebra;
                const statusColor = status === 'Unseen'
                  ? T.statusUnseen
                  : status === 'Incomplete'
                    ? T.statusIncomplete
                    : '#000000';
                return (
                  <tr
                    key={q.id ?? idx}
                    onClick={() => onJump(idx)}
                    style={{ background: bg, cursor: 'pointer' }}
                  >
                    <td style={{ ...tdBase, fontWeight: isCurrent ? 700 : 400, background: bg }}>
                      Question {idx + 1}
                    </td>
                    <td style={{ ...tdBase, color: isCurrent ? '#000000' : statusColor, background: bg }}>
                      {status}
                    </td>
                    {hideFlagged ? null : (
                      <td style={{ ...tdBase, background: bg }}>{flagged ? 'Yes' : ''}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div
          style={{
            flex: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px 10px',
            background: chrome.titleBar,
            color: chrome.chromeFg,
            fontSize: 13,
          }}
        >
          <span>{unseenIncomplete} Unseen/Incomplete</span>
          <button
            type="button"
            onClick={onClose}
            style={{
              minWidth: 64,
              height: 24,
              background: 'transparent',
              color: chrome.chromeFg,
              border: `1px solid ${chrome.chromeFg}`,
              fontFamily: 'Tahoma, "Segoe UI", sans-serif',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
        <div
          className="tmua-pearson-navigator-resize-handle"
          onMouseDown={(e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startY = e.clientY;
            const startW = size.w;
            const startH = size.h;
            const onMove = (ev) => {
              setSize({
                w: Math.max(480, startW + (ev.clientX - startX)),
                h: Math.max(280, startH + (ev.clientY - startY)),
              });
            };
            const onUp = () => {
              window.removeEventListener('mousemove', onMove);
              window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
          }}
          title="Resize"
          style={{
            position: 'absolute',
            right: 2,
            bottom: 2,
            width: 14,
            height: 14,
            cursor: 'nwse-resize',
            background: `linear-gradient(135deg, transparent 55%, ${chrome.chromeFg} 55%)`,
            opacity: 0.9,
          }}
        />
      </div>
    </div>
  );
}
